import { ClipboardEvent, Fragment, KeyboardEvent, useRef } from 'react'
import { Controller, FieldError, FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { FormError, Label } from 'shared/atoms'
import { cn } from 'utils/style'

type BankAccountPartsInputProps = {
	form: UseFormReturn<FieldValues>
	placeholder: string
	country?: string | null
	error?: FieldError
}

const SEPARATOR = /[-\s]/
const PART_SEPARATOR = '-'

const inputClassName =
	'h-10 w-full min-w-0 rounded-md border border-border bg-background px-3 text-sm tabular-nums text-foreground outline-none transition placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-primary/20'

export const getBankAccountParts = (value: string | undefined, count: number) => {
	const parts = (value ?? '').split(SEPARATOR)
	const tail = parts.slice(count - 1).join(PART_SEPARATOR)

	return Array.from({ length: count }, (_, index) =>
		index < count - 1 ? (parts[index] ?? '') : tail
	)
}

export const joinBankAccountParts = (parts: string[]) => parts.join(' ').replace(/\s+$/, '')

export const parsePastedBankAccount = (text: string, lengths: number[]) => {
	const trimmed = text.trim()
	const total = lengths.reduce((sum, length) => sum + length, 0)
	let parts = trimmed.split(/[-\s]+/)

	if (parts.length === 1 && /^\d+$/.test(trimmed) && trimmed.length === total) {
		let offset = 0

		parts = lengths.map((length) => trimmed.slice(offset, (offset += length)))
	}

	return parts.length === lengths.length ? parts : null
}

export const BankAccountPartsInput = ({
	form,
	placeholder,
	country,
	error
}: BankAccountPartsInputProps) => {
	const { t } = useTranslation()
	const { control } = form
	const placeholderParts = placeholder.split(PART_SEPARATOR)
	const lengths = placeholderParts.map((part) => part.length)
	const lastIndex = placeholderParts.length - 1
	const inputs = useRef<(HTMLInputElement | null)[]>([])

	const partLabels: Record<string, string[]> = {
		IS: [t('Bank'), t('Ledger'), t('Account number')],
		DK: [t('Reg. no.'), t('Account number')],
		SE: [t('Clearing no.'), t('Account number')]
	}
	const labels = (country && partLabels[country]) || []
	const hint = country === 'IS' ? t('Leading zeros can be left out.') : undefined

	const focusPart = (index: number) => {
		const input = inputs.current[index]

		input?.focus()
		input?.select()
	}

	return (
		<div className="flex flex-col gap-y-1.5 sm:col-span-2">
			<Label htmlFor="bank_account" text={t('Bank account')} />
			<Controller
				name="bank_account"
				control={control}
				render={({ field }) => {
					const parts = getBankAccountParts(field.value, placeholderParts.length)

					const updatePart = (index: number, raw: string) => {
						const isLast = index === lastIndex
						const clean = raw.replace(isLast ? /[^0-9-]/g : /\D/g, '')
						const next = [...parts]

						next[index] = isLast ? clean : clean.slice(0, lengths[index])
						field.onChange(joinBankAccountParts(next))

						if (!isLast && next[index].length === lengths[index]) focusPart(index + 1)
					}

					const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
						if (event.key === 'Backspace' && !parts[index] && index > 0) {
							event.preventDefault()
							focusPart(index - 1)
						}

						if ((event.key === '-' || event.key === ' ') && index < lastIndex) {
							event.preventDefault()
							if (parts[index]) focusPart(index + 1)
						}
					}

					const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
						const pasted = parsePastedBankAccount(event.clipboardData.getData('text'), lengths)

						if (!pasted) return

						event.preventDefault()
						field.onChange(joinBankAccountParts(pasted))
						focusPart(lastIndex)
					}

					return (
						<div role="group" aria-label={t('Bank account')} className="flex items-end gap-2">
							{placeholderParts.map((placeholderPart, index) => (
								<Fragment key={index}>
									{index > 0 && (
										<span aria-hidden className="pb-2.5 text-muted-foreground">
											{PART_SEPARATOR}
										</span>
									)}
									<div
										className={cn(
											'flex flex-col gap-y-1',
											index < lastIndex ? 'shrink-0' : 'min-w-0 flex-1'
										)}>
										{labels[index] && (
											<span className="whitespace-nowrap text-[11px] normal-case text-muted-foreground">
												{labels[index]}
											</span>
										)}
										<input
											id={index === 0 ? 'bank_account' : `bank_account_${index + 1}`}
											ref={(input) => {
												inputs.current[index] = input
											}}
											type="text"
											inputMode="numeric"
											autoComplete="off"
											placeholder={placeholderPart}
											aria-label={
												labels[index] ??
												`${t('Bank account')} ${index + 1}/${placeholderParts.length}`
											}
											value={parts[index]}
											style={
												index < lastIndex
													? { width: `calc(${placeholderPart.length}ch + 28px)` }
													: undefined
											}
											className={cn(
												inputClassName,
												error && 'border-danger focus:border-danger focus:ring-danger/20'
											)}
											onChange={(event) => updatePart(index, event.target.value)}
											onKeyDown={(event) => handleKeyDown(index, event)}
											onPaste={handlePaste}
											onBlur={field.onBlur}
										/>
									</div>
								</Fragment>
							))}
						</div>
					)
				}}
			/>
			{hint && !error && <span className="text-[11px] text-muted-foreground">{hint}</span>}
			<FormError text={error?.message} />
		</div>
	)
}
